import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentLocation } from '../services/location.service';
import {
  ADMIN_CREATE_TIME_CLOCK_ENTRY,
  ADMIN_UPDATE_TIME_CLOCK_ENTRY,
  CLOCK_IN,
  CLOCK_OUT,
  CURRENTLY_CLOCKED_IN,
  MY_TIME_CLOCK_SUMMARY,
  TIME_CLOCK_ENTRIES,
  TIME_CLOCK_USER_SUMMARIES,
} from '../graphql/time-clock';
import { GET_IS_TIME_CLOCK_GLOBALLY_ENABLED } from '../graphql/settings';

const OFFLINE_QUEUE_KEY = 'sweetgrass_time_clock_offline_queue';
const REMOTE_ENTRY_LABEL = 'Remote Entry';
const LOCATION_OPTIONAL_MESSAGE = 'Please enable location when possible. This punch was saved without GPS and marked as Remote Entry.';
const ALL_FILTER_VALUE = 'all';
const EMPLOYEE_SORT_OPTIONS = {
  RECENT: 'recent',
  ALPHABETICAL: 'alphabetical',
  HOURS: 'hours',
};
const AREA_LABELS = {
  KIAWAH: 'Kiawah',
  ISLE_OF_PALMS: 'Isle of Palms',
};
const COMBINED_AREA_BY_ASSIGNMENT = {
  KIAWAH: 'KIAWAH',
  SEABROOK_ISLAND: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS',
};
const COMBINED_AREA_ORDER = ['KIAWAH', 'ISLE_OF_PALMS'];
const TIME_CLOCK_LOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 8000,
  maximumAge: 60000,
};
const PAY_PERIOD_WEEK_OPTIONS = { weekStartsOn: 1 };

const minutesToHours = (minutes) => {
  const totalMinutes = Math.max(0, Math.round(Number(minutes || 0)));
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const roleLabel = (role) => (
  String(role || 'USER')
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
);

const normalizeAreas = (areas) => (Array.isArray(areas) ? areas.filter(Boolean) : []);

const areaLabel = (area) => AREA_LABELS[area] || String(area || '').replace(/_/g, ' ');

const combinedAreas = (areas) => {
  const assignedAreas = new Set(
    normalizeAreas(areas)
      .map((area) => COMBINED_AREA_BY_ASSIGNMENT[area])
      .filter(Boolean),
  );

  return COMBINED_AREA_ORDER.filter((area) => assignedAreas.has(area));
};

const areasLabel = (areas) => {
  const groupedAreas = combinedAreas(areas);
  if (groupedAreas.length === 0) return 'Unassigned';
  return groupedAreas.map(areaLabel).join(', ');
};

const isQueueableNetworkError = (error) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;

  const networkError = error?.networkError;
  if (networkError) {
    const statusCode = networkError.statusCode || networkError.status || networkError.response?.status;
    if (statusCode === 401 || statusCode === 403) return false;
    return !statusCode || statusCode === 408 || statusCode >= 500;
  }

  const message = String(error?.message || '').toLowerCase();
  return [
    'failed to fetch',
    'load failed',
    'network request failed',
    'networkerror',
    'timeout',
    'timed out',
  ].some((text) => message.includes(text));
};

const metersLabel = (meters) => {
  if (meters === null || meters === undefined) return null;
  const value = Number(meters);
  if (Number.isNaN(value)) return null;
  if (value >= 1000) return `${(value / 1000).toFixed(2)} km`;
  return `${Math.round(value)} m`;
};

const officeDistanceLabel = (entry) => {
  const labels = [];
  const clockInDistance = metersLabel(entry.clockInDistanceMeters);
  const clockOutDistance = metersLabel(entry.clockOutDistanceMeters);
  if (clockInDistance) labels.push(`In: ${clockInDistance}`);
  if (clockOutDistance) labels.push(`Out: ${clockOutDistance}`);
  return labels.join(' / ');
};

const officeLabel = (entry) => {
  if (entry.clockInOutsideRadius || entry.clockOutOutsideRadius) return REMOTE_ENTRY_LABEL;
  return entry.clockInOfficeName || entry.clockOutOfficeName || '-';
};

const dateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, "yyyy-MM-dd'T'HH:mm");
};

const readQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeQueue = (queue) => {
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
};

const flagLabels = (entry) => {
  const flags = [];
  if (entry.clockInOffline || entry.clockOutOffline) flags.push('Offline');
  if (entry.clockInOutsideRadius || entry.clockOutOutsideRadius) flags.push('Outside radius');
  if (entry.isEdited) flags.push('Edited');
  if (entry.status === 'ACTIVE') flags.push('Open');
  return flags;
};

const hasNote = (entry) => Boolean(entry?.notes?.trim());

const entryUpdatedTimestamp = (entry) => {
  const timestamps = [
    entry?.updatedAt,
    entry?.editedAt,
    entry?.clockedOutAt,
    entry?.clockedInAt,
  ]
    .map((value) => {
      if (!value) return 0;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    });

  return Math.max(...timestamps, 0);
};

function EntryIndicators({ entry }) {
  const flags = flagLabels(entry);
  const noteAdded = hasNote(entry);

  if (flags.length === 0 && !noteAdded) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((flag) => (
        <span key={flag} className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          {flag}
        </span>
      ))}
      {noteAdded && (
        <span
          className="rounded bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800"
          title={entry.notes}
          aria-label={`Note added: ${entry.notes}`}
        >
          Note
        </span>
      )}
    </div>
  );
}

function TimeClockEntriesSlidePanel({
  isOpen,
  employee,
  entries,
  weekStart,
  weekEnd,
  onClose,
  onEdit,
  isEditOpen,
}) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && !isEditOpen) onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isEditOpen, isOpen, onClose]);

  useEffect(() => {
    if (isOpen && employee) {
      setShouldRender(true);
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    }

    if (!isOpen) {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 440);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [employee, isOpen]);

  if (!shouldRender || !employee) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black transition-opacity duration-300 ${
          isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-5xl transform bg-gray-50 shadow-2xl transition-transform duration-[440ms] ease-in-out ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="time-clock-employee-logs-title"
      >
        <div className="flex h-full flex-col">
          <header className="flex flex-col gap-4 border-b border-gray-200 bg-white px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
            <div className="pr-12">
              <h2 id="time-clock-employee-logs-title" className="text-xl font-semibold text-gray-900">
                {employee.userName}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Time logs for {weekStart} through {weekEnd}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-700">
                <span><span className="font-medium">Total:</span> {minutesToHours(employee.totalMinutes || 0)}</span>
                <span><span className="font-medium">Entries:</span> {entries.length}</span>
                <span><span className="font-medium">Flags:</span> {employee.flaggedEntriesCount}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-500 shadow hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-label="Close time logs"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <section className="overflow-hidden rounded-lg bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Clock In</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Clock Out</th>
                      <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Hours</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Office</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Flags</th>
                      <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {entries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="whitespace-nowrap px-5 py-3 text-sm text-gray-700">
                          {format(new Date(entry.clockedInAt), 'MMM d, h:mm a')}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3 text-sm text-gray-700">
                          {entry.clockedOutAt ? format(new Date(entry.clockedOutAt), 'MMM d, h:mm a') : '-'}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3 text-right text-sm text-gray-700">
                          {minutesToHours(entry.durationMinutes || 0)}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700">
                          <div>{officeLabel(entry)}</div>
                          {officeDistanceLabel(entry) && (
                            <div className="mt-0.5 text-xs text-gray-500">{officeDistanceLabel(entry)}</div>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700">
                          <EntryIndicators entry={entry} />
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => onEdit(entry)}
                            className="text-sm font-medium text-sky-700 hover:text-sky-900"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </aside>
    </>
  );
}

function TimeClockEntryModal({ entry, users = [], onClose, onSaved }) {
  const [formData, setFormData] = useState(() => ({
    userId: entry?.userId || '',
    clockedInAt: dateTimeLocalValue(entry?.clockedInAt),
    clockedOutAt: dateTimeLocalValue(entry?.clockedOutAt),
    notes: entry?.notes || '',
  }));

  const [adminUpdateEntry, { loading: updating }] = useMutation(ADMIN_UPDATE_TIME_CLOCK_ENTRY);
  const [adminCreateEntry, { loading: creating }] = useMutation(ADMIN_CREATE_TIME_CLOCK_ENTRY);

  const isNew = !entry;
  const isSaving = updating || creating;

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (isNew) {
        await adminCreateEntry({
          variables: {
            input: {
              userId: Number(formData.userId),
              clockedInAt: new Date(formData.clockedInAt).toISOString(),
              clockedOutAt: new Date(formData.clockedOutAt).toISOString(),
              notes: formData.notes || null,
            },
          },
        });
      } else {
        await adminUpdateEntry({
          variables: {
            input: {
              entryId: entry.id,
              clockedInAt: formData.clockedInAt ? new Date(formData.clockedInAt).toISOString() : null,
              clockedOutAt: formData.clockedOutAt ? new Date(formData.clockedOutAt).toISOString() : null,
              notes: formData.notes || null,
            },
          },
        });
      }
      toast.success(isNew ? 'Time entry added' : 'Time entry updated');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save time entry');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 bg-opacity-40 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{isNew ? 'Add Time Entry' : 'Edit Time Entry'}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <span className="text-2xl leading-none">&times;</span>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          {isNew && (
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="timeClockUserId">Team Member</label>
              <select
                id="timeClockUserId"
                value={formData.userId}
                onChange={(event) => setFormData({ ...formData, userId: event.target.value })}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 p-2"
              >
                <option value="">Select team member</option>
                {users.map((user) => (
                  <option key={user.userId} value={user.userId}>{user.userName}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="clockedInAt">Clock In</label>
              <input
                id="clockedInAt"
                type="datetime-local"
                value={formData.clockedInAt}
                onChange={(event) => setFormData({ ...formData, clockedInAt: event.target.value })}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="clockedOutAt">Clock Out</label>
              <input
                id="clockedOutAt"
                type="datetime-local"
                value={formData.clockedOutAt}
                onChange={(event) => setFormData({ ...formData, clockedOutAt: event.target.value })}
                required={isNew}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="timeClockNotes">Notes</label>
            <textarea
              id="timeClockNotes"
              value={formData.notes}
              onChange={(event) => setFormData({ ...formData, notes: event.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700">
              Cancel
            </button>
            <button type="submit" disabled={isSaving} className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TimeClock() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [weekStart, setWeekStart] = useState(() => format(startOfWeek(new Date(), PAY_PERIOD_WEEK_OPTIONS), 'yyyy-MM-dd'));
  const [weekEnd, setWeekEnd] = useState(() => format(endOfWeek(new Date(), PAY_PERIOD_WEEK_OPTIONS), 'yyyy-MM-dd'));
  const [selectedRole, setSelectedRole] = useState(ALL_FILTER_VALUE);
  const [selectedArea, setSelectedArea] = useState(ALL_FILTER_VALUE);
  const [employeeSort, setEmployeeSort] = useState(EMPLOYEE_SORT_OPTIONS.RECENT);
  const [permissionMessage, setPermissionMessage] = useState('');
  const [offlineQueue, setOfflineQueue] = useState(readQueue);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [isEmployeeLogsOpen, setIsEmployeeLogsOpen] = useState(false);
  const [isPunching, setIsPunching] = useState(false);

  const [clockIn] = useMutation(CLOCK_IN);
  const [clockOut] = useMutation(CLOCK_OUT);

  const globalEnabledQuery = useQuery(GET_IS_TIME_CLOCK_GLOBALLY_ENABLED, {
    skip: !user?.requiresTimeClock && !isAdmin,
    fetchPolicy: 'network-only',
    pollInterval: user?.requiresTimeClock || isAdmin ? 60000 : 0,
  });

  const isGlobalSettingResolved = typeof globalEnabledQuery.data?.isTimeClockGloballyEnabled === 'boolean';
  const isTimeClockGloballyEnabled = isGlobalSettingResolved && globalEnabledQuery.data.isTimeClockGloballyEnabled;
  const canUseTimeClock = Boolean(user?.requiresTimeClock) && isTimeClockGloballyEnabled;

  const summaryQuery = useQuery(MY_TIME_CLOCK_SUMMARY, {
    variables: { startDate: weekStart, endDate: weekEnd },
    skip: !canUseTimeClock,
    fetchPolicy: 'cache-and-network',
    pollInterval: canUseTimeClock ? 30000 : 0,
  });

  const entriesQuery = useQuery(TIME_CLOCK_ENTRIES, {
    variables: { input: { startDate: weekStart, endDate: weekEnd } },
    skip: !isAdmin,
    fetchPolicy: 'cache-and-network',
    pollInterval: isAdmin ? 30000 : 0,
  });

  const activeQuery = useQuery(CURRENTLY_CLOCKED_IN, {
    skip: !isAdmin,
    fetchPolicy: 'cache-and-network',
    pollInterval: isAdmin ? 30000 : 0,
  });

  const userSummariesQuery = useQuery(TIME_CLOCK_USER_SUMMARIES, {
    variables: { startDate: weekStart, endDate: weekEnd },
    skip: !isAdmin,
    fetchPolicy: 'cache-and-network',
    pollInterval: isAdmin ? 30000 : 0,
  });

  const refetchSummary = summaryQuery.refetch;
  const refetchEntries = entriesQuery.refetch;
  const refetchActive = activeQuery.refetch;
  const refetchUserSummaries = userSummariesQuery.refetch;

  const lastQueuedPunch = useMemo(() => {
    const queue = offlineQueue.filter(item => item.userId === user?.id);
    return queue[queue.length - 1];
  }, [offlineQueue, user?.id]);

  const hasOfflineOpenPunch = lastQueuedPunch?.action === 'CLOCK_IN';
  const hasOfflineClosePunch = lastQueuedPunch?.action === 'CLOCK_OUT';

  const activeEntry = summaryQuery.data?.myTimeClockSummary?.activeEntry;
  const isClockedIn = hasOfflineClosePunch ? false : Boolean(activeEntry || hasOfflineOpenPunch);

  const refreshAll = useCallback(async () => {
    const refetches = [];
    if (canUseTimeClock && refetchSummary) refetches.push(refetchSummary());
    if (isAdmin) {
      if (refetchEntries) refetches.push(refetchEntries());
      if (refetchActive) refetches.push(refetchActive());
      if (refetchUserSummaries) refetches.push(refetchUserSummaries());
    }
    await Promise.all(refetches);
  }, [canUseTimeClock, isAdmin, refetchActive, refetchEntries, refetchSummary, refetchUserSummaries]);

  const syncOfflineQueue = useCallback(async () => {
    if (!canUseTimeClock) return;
    const queue = readQueue();
    if (!queue.length || !navigator.onLine) return;

    const remaining = [];
    for (let index = 0; index < queue.length; index += 1) {
      const item = queue[index];
      try {
        const mutation = item.action === 'CLOCK_IN' ? clockIn : clockOut;
        await mutation({ variables: { input: item.input } });
      } catch (error) {
        remaining.push(...queue.slice(index));
        break;
      }
    }
    writeQueue(remaining);
    setOfflineQueue(remaining);
    if (queue.length !== remaining.length) {
      toast.success('Offline time-clock punches synced');
      refreshAll();
    }
  }, [canUseTimeClock, clockIn, clockOut, refreshAll]);

  useEffect(() => {
    syncOfflineQueue();
    const handleOnline = () => syncOfflineQueue();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncOfflineQueue]);

  const getPunchInput = async (isOffline) => {
    setPermissionMessage('');
    const baseInput = {
      isOffline,
      source: isOffline ? 'OFFLINE_SYNC' : 'WEB',
      deviceTimestamp: new Date().toISOString(),
    };

    try {
      const location = await getCurrentLocation(TIME_CLOCK_LOCATION_OPTIONS);
      return {
        ...baseInput,
        latitude: location.latitude,
        longitude: location.longitude,
        accuracyMeters: location.accuracy,
      };
    } catch (error) {
      setPermissionMessage(LOCATION_OPTIONAL_MESSAGE);
      return baseInput;
    }
  };

  const queueOfflinePunch = (action, input) => {
    const offlineInput = {
      ...input,
      isOffline: true,
      source: 'OFFLINE_SYNC',
    };
    const nextQueue = [...readQueue(), { id: `${Date.now()}-${action}`, userId: user.id, action, input: offlineInput }];
    writeQueue(nextQueue);
    setOfflineQueue(nextQueue);
    toast.success('Punch saved offline');
  };

  const handlePunch = async () => {
    if (!canUseTimeClock) return;
    setIsPunching(true);
    let action;
    let input;
    try {
      const isOffline = !navigator.onLine;
      input = await getPunchInput(isOffline);
      action = isClockedIn ? 'CLOCK_OUT' : 'CLOCK_IN';
      if (isOffline) {
        queueOfflinePunch(action, input);
      } else {
        const mutation = isClockedIn ? clockOut : clockIn;
        await mutation({ variables: { input } });
        toast.success(isClockedIn ? 'Punched out' : 'Punched in');
        await refreshAll();
      }
    } catch (error) {
      if (action && input && isQueueableNetworkError(error)) {
        queueOfflinePunch(action, input);
      } else if (!permissionMessage) {
        toast.error(error.message || 'Unable to save punch');
      }
    } finally {
      setIsPunching(false);
    }
  };

  const entries = useMemo(
    () => entriesQuery.data?.timeClockEntries || [],
    [entriesQuery.data],
  );
  const currentlyClockedIn = useMemo(
    () => activeQuery.data?.currentlyClockedIn || [],
    [activeQuery.data],
  );
  const currentlyClockedInUserIds = useMemo(() => (
    new Set(currentlyClockedIn.map((entry) => String(entry.userId)))
  ), [currentlyClockedIn]);
  const userSummaries = useMemo(
    () => userSummariesQuery.data?.timeClockUserSummaries || [],
    [userSummariesQuery.data],
  );
  const myEntries = useMemo(
    () => summaryQuery.data?.myTimeClockSummary?.entries || [],
    [summaryQuery.data],
  );

  const roleOptions = useMemo(() => {
    const roles = new Set();
    userSummaries.forEach((summary) => {
      if (summary.role) roles.add(summary.role);
    });
    entries.forEach((entry) => {
      if (entry.user?.role) roles.add(entry.user.role);
    });
    return Array.from(roles).sort((a, b) => roleLabel(a).localeCompare(roleLabel(b)));
  }, [entries, userSummaries]);

  const areaOptions = useMemo(() => {
    const areas = new Set();
    userSummaries.forEach((summary) => combinedAreas(summary.areas).forEach((area) => areas.add(area)));
    entries.forEach((entry) => combinedAreas(entry.user?.areas).forEach((area) => areas.add(area)));
    return COMBINED_AREA_ORDER.filter((area) => areas.has(area));
  }, [entries, userSummaries]);

  const userMatchesBaseFilters = useCallback((candidate) => {
    const candidateRole = candidate.role || candidate.user?.role;
    const candidateAreas = combinedAreas(candidate.areas || candidate.user?.areas);

    if (selectedRole !== ALL_FILTER_VALUE && candidateRole !== selectedRole) return false;
    if (selectedArea !== ALL_FILTER_VALUE && !candidateAreas.includes(selectedArea)) return false;
    return true;
  }, [selectedArea, selectedRole]);

  const userIdsWithEntries = useMemo(() => (
    new Set(entries.map((entry) => String(entry.userId)))
  ), [entries]);

  const filteredUserSummaries = useMemo(() => (
    userSummaries.filter((summary) => (
      userIdsWithEntries.has(String(summary.userId))
      && userMatchesBaseFilters(summary)
    ))
  ), [userIdsWithEntries, userMatchesBaseFilters, userSummaries]);

  const latestEntryTimestampByUserId = useMemo(() => {
    const timestamps = new Map();
    entries.forEach((entry) => {
      const timestamp = entryUpdatedTimestamp(entry);
      const userId = String(entry.userId || '');
      if (!userId) return;
      if (timestamp > (timestamps.get(userId) || 0)) {
        timestamps.set(userId, timestamp);
      }
    });
    return timestamps;
  }, [entries]);

  const sortedUserSummaries = useMemo(() => {
    const rows = [...filteredUserSummaries];

    rows.sort((a, b) => {
      const nameCompare = a.userName.localeCompare(b.userName);

      if (employeeSort === EMPLOYEE_SORT_OPTIONS.ALPHABETICAL) {
        return nameCompare;
      }

      if (employeeSort === EMPLOYEE_SORT_OPTIONS.HOURS) {
        const hoursCompare = Number(b.totalMinutes || 0) - Number(a.totalMinutes || 0);
        return hoursCompare || nameCompare;
      }

      const aTimestamp = latestEntryTimestampByUserId.get(String(a.userId)) || 0;
      const bTimestamp = latestEntryTimestampByUserId.get(String(b.userId)) || 0;
      const recentCompare = bTimestamp - aTimestamp;
      return recentCompare || nameCompare;
    });

    return rows;
  }, [employeeSort, filteredUserSummaries, latestEntryTimestampByUserId]);

  const selectedEmployee = useMemo(() => (
    userSummaries.find((summary) => String(summary.userId) === String(selectedEmployeeId)) || null
  ), [selectedEmployeeId, userSummaries]);

  const selectedEmployeeEntries = useMemo(() => (
    selectedEmployee
      ? entries.filter((entry) => String(entry.userId) === String(selectedEmployee.userId))
      : []
  ), [entries, selectedEmployee]);

  const closeEmployeeLogs = useCallback(() => {
    setIsEmployeeLogsOpen(false);
  }, []);

  const resetAdminFilters = () => {
    const today = new Date();
    setWeekStart(format(startOfWeek(today, PAY_PERIOD_WEEK_OPTIONS), 'yyyy-MM-dd'));
    setWeekEnd(format(endOfWeek(today, PAY_PERIOD_WEEK_OPTIONS), 'yyyy-MM-dd'));
    setSelectedRole(ALL_FILTER_VALUE);
    setSelectedArea(ALL_FILTER_VALUE);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isAdmin ? 'Time Clock Management' : 'My Time Clock'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {isAdmin
                ? 'Weekly hours, active punches, and flagged time logs.'
                : 'Your weekly hours and punch history.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {isAdmin && (
              <Link
                to="/dashboard/hourly-payroll"
                className="inline-flex items-center rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
              >
                Hourly Payroll Reports
              </Link>
            )}
            {!isAdmin && (
              <>
                <input
                  type="date"
                  value={weekStart}
                  onChange={(event) => setWeekStart(event.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                  aria-label="Start date"
                />
                <input
                  type="date"
                  value={weekEnd}
                  onChange={(event) => setWeekEnd(event.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                  aria-label="End date"
                />
              </>
            )}
          </div>
        </div>

        {user?.requiresTimeClock && !isTimeClockGloballyEnabled && (
          <section className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Timeclock is currently turned off. Your employee timeclock settings are saved, but punch tracking is not available right now.
          </section>
        )}

        {canUseTimeClock && (
          <section className="mb-6 rounded-lg bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-sm font-medium uppercase tracking-wide text-gray-500">
                  {isClockedIn ? 'Clocked In' : 'Clocked Out'}
                </div>
                <div className="mt-1 text-3xl font-semibold text-gray-900">
                  {minutesToHours(summaryQuery.data?.myTimeClockSummary?.totalMinutes || 0)}
                </div>
              </div>
              <button
                type="button"
                onClick={handlePunch}
                disabled={isPunching}
                className={`rounded-md px-5 py-3 text-sm font-semibold text-white disabled:opacity-60 ${
                  isClockedIn ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {isPunching ? 'Saving...' : isClockedIn ? 'Punch Out' : 'Punch In'}
              </button>
            </div>
            {permissionMessage && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <div>{permissionMessage}</div>
              </div>
            )}
            {offlineQueue.length > 0 && (
              <div className="mt-4 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
                {offlineQueue.length} offline punch{offlineQueue.length === 1 ? '' : 'es'} pending sync.
              </div>
            )}
          </section>
        )}

        {canUseTimeClock && !isAdmin && (
          <section className="rounded-lg bg-white shadow-sm">
            <div className="border-b px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">My Punch History</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Punch In</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Punch Out</th>
                    <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Hours</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Office</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {myEntries.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-5 py-6 text-center text-sm text-gray-500">
                        No punches found for this date range.
                      </td>
                    </tr>
                  ) : (
                    myEntries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-5 py-3 text-sm text-gray-700">{format(new Date(entry.clockedInAt), 'MMM d, h:mm a')}</td>
                        <td className="px-5 py-3 text-sm text-gray-700">
                          {entry.clockedOutAt ? format(new Date(entry.clockedOutAt), 'MMM d, h:mm a') : '-'}
                        </td>
                        <td className="px-5 py-3 text-right text-sm text-gray-700">{minutesToHours(entry.durationMinutes || 0)}</td>
                        <td className="px-5 py-3 text-sm text-gray-700">
                          <div>{officeLabel(entry)}</div>
                          {officeDistanceLabel(entry) && (
                            <div className="mt-0.5 text-xs text-gray-500">{officeDistanceLabel(entry)}</div>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700">
                          <EntryIndicators entry={entry} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {isAdmin && (
          <>
            <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-lg bg-white p-5 shadow-sm">
                <div className="text-sm font-medium text-gray-500">Currently Clocked In</div>
                <div className="mt-2 text-3xl font-semibold text-gray-900">{currentlyClockedIn.length}</div>
              </div>
              <div className="rounded-lg bg-white p-5 shadow-sm">
                <div className="text-sm font-medium text-gray-500">Tracked Users</div>
                <div className="mt-2 text-3xl font-semibold text-gray-900">{userSummaries.length}</div>
              </div>
              <div className="rounded-lg bg-white p-5 shadow-sm">
                <div className="text-sm font-medium text-gray-500">Flagged Entries</div>
                <div className="mt-2 text-3xl font-semibold text-gray-900">
                  {userSummaries.reduce((sum, item) => sum + item.flaggedEntriesCount, 0)}
                </div>
              </div>
            </section>

            <section className="mb-6 rounded-lg bg-white p-5 shadow-sm">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <button
                  type="button"
                  onClick={resetAdminFilters}
                  className="self-start text-sm font-medium text-sky-700 hover:text-sky-900 sm:self-auto"
                >
                  Reset filters
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className="block text-xs font-medium uppercase text-gray-500" htmlFor="timeClockStartDate">Start Date</label>
                  <input
                    id="timeClockStartDate"
                    type="date"
                    value={weekStart}
                    onChange={(event) => setWeekStart(event.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase text-gray-500" htmlFor="timeClockEndDate">End Date</label>
                  <input
                    id="timeClockEndDate"
                    type="date"
                    value={weekEnd}
                    onChange={(event) => setWeekEnd(event.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase text-gray-500" htmlFor="timeClockRoleFilter">Role</label>
                  <select
                    id="timeClockRoleFilter"
                    value={selectedRole}
                    onChange={(event) => setSelectedRole(event.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value={ALL_FILTER_VALUE}>All roles</option>
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>{roleLabel(role)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase text-gray-500" htmlFor="timeClockAreaFilter">Area</label>
                  <select
                    id="timeClockAreaFilter"
                    value={selectedArea}
                    onChange={(event) => setSelectedArea(event.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value={ALL_FILTER_VALUE}>All areas</option>
                    {areaOptions.map((area) => (
                      <option key={area} value={area}>{areaLabel(area)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="mb-6 rounded-lg bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Employee Totals</h2>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <label className="flex items-center gap-2 text-sm text-gray-700" htmlFor="timeClockEmployeeSort">
                    <span className="font-medium">Sort</span>
                    <select
                      id="timeClockEmployeeSort"
                      value={employeeSort}
                      onChange={(event) => setEmployeeSort(event.target.value)}
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value={EMPLOYEE_SORT_OPTIONS.RECENT}>Most recently updated</option>
                      <option value={EMPLOYEE_SORT_OPTIONS.ALPHABETICAL}>Alphabetical</option>
                      <option value={EMPLOYEE_SORT_OPTIONS.HOURS}>Hours: most logged</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsCreatingEntry(true)}
                    className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
                  >
                    Add Entry
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Team Member</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Role</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Area</th>
                      <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Hours</th>
                      <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Flags</th>
                      <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {sortedUserSummaries.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-5 py-6 text-center text-sm text-gray-500">
                          No employees match the selected filters.
                        </td>
                      </tr>
                    ) : (
                      sortedUserSummaries.map((summary) => (
                        <tr key={summary.userId}>
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">
                            <div className="flex flex-wrap items-center gap-2">
                              <span>{summary.userName}</span>
                              {currentlyClockedInUserIds.has(String(summary.userId)) && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                                  <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" aria-hidden="true" />
                                  Clock running
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-700">{roleLabel(summary.role)}</td>
                          <td className="px-5 py-3 text-sm text-gray-700">{areasLabel(summary.areas)}</td>
                          <td className="px-5 py-3 text-right text-sm text-gray-700">{minutesToHours(summary.totalMinutes || 0)}</td>
                          <td className="px-5 py-3 text-right text-sm text-gray-700">{summary.flaggedEntriesCount}</td>
                          <td className="px-5 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedEmployeeId(summary.userId);
                                setIsEmployeeLogsOpen(true);
                              }}
                              className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 hover:border-sky-300 hover:bg-sky-100"
                            >
                              View logs
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>

      <TimeClockEntriesSlidePanel
        isOpen={isEmployeeLogsOpen}
        employee={selectedEmployee}
        entries={selectedEmployeeEntries}
        weekStart={weekStart}
        weekEnd={weekEnd}
        onClose={closeEmployeeLogs}
        onEdit={setEditingEntry}
        isEditOpen={Boolean(editingEntry)}
      />

      {(editingEntry || isCreatingEntry) && (
        <TimeClockEntryModal
          entry={editingEntry}
          users={userSummaries}
          onClose={() => {
            setEditingEntry(null);
            setIsCreatingEntry(false);
          }}
          onSaved={refreshAll}
        />
      )}
    </div>
  );
}
